import { getOrgTermsContent } from '../content/utils.js';

// Order follows the 2×2 grid of the design (left to right, top to bottom)
export const DUPLICATION_OPTIONS = [
  { value: 'participants', key: 'participants' },
  { value: 'stakeholders', key: 'stakeholders' },
  { value: 'feedback_form', key: 'feedbackForm' },
  { value: 'linked_files', key: 'linkedFiles' },
];

export const ALL_OPTIONS = DUPLICATION_OPTIONS.map(({ value }) => value);

// Org terms are applied to the template only, so user content (titles) is never rewritten.
export const duplicationText = (key, params = {}) =>
  Object.entries(params).reduce(
    (text, [name, value]) => text.replace(`{${name}}`, value),
    getOrgTermsContent(`duplicateInitiative.${key}`)
  );

export const optionLabel = (key) => duplicationText(`options.${key}`);

// One warning per cleared option, in the order of the grid.
// "Nothing selected" replaces every other warning, as in the project duplication.
export const getDuplicationWarnings = (options) => {
  if (!options.length) {
    return [
      {
        key: 'nothingSelected',
        title: null,
        body: duplicationText('warnings.nothingSelected'),
      },
    ];
  }

  return DUPLICATION_OPTIONS.filter(
    ({ value }) => !options.includes(value)
  ).map(({ key }) => ({
    key,
    title: duplicationText(`warnings.${key}Title`),
    body: duplicationText(`warnings.${key}`),
  }));
};

export const manageInitiativesFlag = (type) =>
  `manage${type.charAt(0).toUpperCase() + type.slice(1)}Initiatives`;

// Projects a copy can go to: active, ready, and where the user may create this initiative type.
export const isDuplicationTarget = (project, type) =>
  !!project &&
  !project.archived &&
  !project.duplicating &&
  !!project[manageInitiativesFlag(type)];

export const buildDuplicatePayload = (values) => ({
  projectId: Number(values.projectId),
  initiative: { title: values.title.trim() },
  sections: values.options,
});

/**
 * What changes in another target project, from the duplication preview:
 * `undefined` while it loads, `null` when it failed (then the lines say what may change).
 * Stakeholders are listed only when they are copied.
 *
 * @returns {{ project: string, loading?: boolean, lines: string[] } | null}
 *   null when nothing changes there
 */
export const getTargetProjectNotice = ({
  targetProject,
  copyStakeholders,
  preview,
}) => {
  const project = targetProject.name;

  if (preview === undefined) return { project, loading: true, lines: [] };

  if (preview === null) {
    return {
      project,
      lines: [
        duplicationText('targetNotice.owner'),
        ...(copyStakeholders
          ? [duplicationText('targetNotice.stakeholdersGeneric')]
          : []),
      ],
    };
  }

  const stakeholders = copyStakeholders ? preview.stakeholdersToAdd : [];
  const lines = [
    ...(preview.ownerChanges
      ? [duplicationText('targetNotice.ownerChanges')]
      : []),
    ...(stakeholders.length
      ? [
          `${duplicationText('targetNotice.stakeholders')} ${stakeholders
            .map(({ fullName }) => fullName)
            .join(', ')}`,
        ]
      : []),
  ];

  return lines.length ? { project, lines } : null;
};

/**
 * Duplicate is offered when the user may create this initiative type in at least one
 * active project. Falls back to the source project's flag when the projects list is not loaded.
 * The source project may be archived: copying out of it is allowed.
 */
export const canDuplicateInitiative = (type, project, projects = []) =>
  !!project?.[manageInitiativesFlag(type)] ||
  projects.some((item) => isDuplicationTarget(item, type));
